import centerOfMass from '@turf/center-of-mass';
import { Feature, GeoJsonProperties, Geometry } from 'geojson';
import { Expression } from 'mapbox-gl';
import React, { useMemo } from 'react';
import { Layer, MapRef, Source, SymbolLayer } from 'react-map-gl';
import { useAppSelector } from '../../../app/hooks';
import { Block } from '../../../services/entities/impl/blfApi/schemas/block';
import { useTranslation } from '../../../services/translation/hooks/useTranslation';
import { selectCurrBlockID } from '../../select/selectSlice';
import { LayerExistenceDict } from '../hooks/useCheckLayers';
import { LatestScanDict } from '../hooks/useFindLatestScans';
import useLayerOrder from '../hooks/useLayerOrder';
import { LayerEnum, selectLayerVisibility } from '../mapSlice';
import { ensureLayerExists } from '../mapUtils';
import { EMPTY_FEATURE_COLLECTION } from '../utils/constants';
import { createFeatureCollection } from '../utils/createFeatureCollection';

interface Props {
  mapRef: MapRef | null;
  layerExistenceDict: LayerExistenceDict;
  blocks: Block[] | undefined;
  latestScans: LatestScanDict;
}

const BlockNameLayer: React.FC<Props> = ({
  mapRef,
  layerExistenceDict,
  blocks,
  latestScans,
}) => {
  const currBlockID = useAppSelector(selectCurrBlockID);
  const layerVisibility = useAppSelector(selectLayerVisibility);
  const layerBeforeId = useLayerOrder(LayerEnum.blockNames, layerExistenceDict);
  const { t } = useTranslation();

  const blocksLabelsFeatureCollection = useMemo(() => {
    if (!blocks) return EMPTY_FEATURE_COLLECTION;
    const features = blocks
      .filter((block) => block.geoPolygon)
      .map((block) => {
        const blockCenter = centerOfMass(block.geoPolygon);

        const latestScan = latestScans[block.id];

        const newFeature: Feature<Geometry, GeoJsonProperties> = {
          type: 'Feature',
          properties: {
            id: block.id,
            name: block.name,
            scanDate: latestScan?.scanDate,
            scanID: latestScan?.scanID,
          },
          geometry: blockCenter.geometry,
        };
        return newFeature;
      });

    if (features && features.length) {
      return createFeatureCollection(features);
    } else {
      return EMPTY_FEATURE_COLLECTION;
    }
  }, [blocks, latestScans]);

  const blockNameLayerVisibility = layerVisibility[LayerEnum.blockNames];

  const blockLabelLayerStyle = useMemo<SymbolLayer | null>(() => {
    if (
      blocksLabelsFeatureCollection &&
      blockNameLayerVisibility &&
      currBlockID === ''
    ) {
      const blockLabelLayerStyle: SymbolLayer = {
        id: LayerEnum.blockNames,
        type: 'symbol',
        layout: {
          'text-field': ['format', ['get', 'name'], { 'font-scale': 0.9 }],
          'text-size': 18,
          'text-anchor': 'center',
          'text-justify': 'center',
          'text-font': [
            'DIN Pro Medium',
            'Open Sans Regular',
            'Arial Unicode MS Regular',
          ],
        },
        paint: {
          'text-color': '#ffffff',
          'text-halo-color': '#000000',
          'text-halo-width': 0.5,
          'text-halo-blur': 0.5,
          'text-opacity':
            blockNameLayerVisibility && currBlockID === '' ? 1 : 0,
        },
      };

      (blockLabelLayerStyle?.layout?.['text-field'] as Expression)?.push(
        '\n',
        ['coalesce', ['get', 'scanDate'], t('No Scans')],
        { 'font-scale': 0.8 }
      );

      return blockLabelLayerStyle;
    } else {
      return null;
    }
  }, [blocksLabelsFeatureCollection, blockNameLayerVisibility, currBlockID, t]);

  return (
    <Source
      key={LayerEnum.blockNames}
      id={LayerEnum.blockNames}
      type='geojson'
      data={blocksLabelsFeatureCollection}
    >
      {blockLabelLayerStyle && (
        <Layer
          key={LayerEnum.blockNames}
          {...blockLabelLayerStyle}
          beforeId={ensureLayerExists(mapRef, layerBeforeId)}
        />
      )}
    </Source>
  );
};

export default BlockNameLayer;
